import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DIRECT_SALE_UPLOAD_ACCEPT,
  DIRECT_SALE_UPLOAD_MAX_MB as MAX_SIZE_MB,
  useDirectSaleUpload,
} from '../useDirectSaleUpload';

/**
 * The upload tool from the design mock: a dropzone with visible outcomes.
 * The panel stays open while the background S3 upload runs and closes on
 * success. What happens to the file lives in useDirectSaleUpload.
 */
export function DirectSaleUploadPanel({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { handleFile, isUploading, error } = useDirectSaleUpload({
    onDone: onClose,
  });

  return (
    <div className="p-3.5">
      <input
        ref={fileInputRef}
        type="file"
        accept={DIRECT_SALE_UPLOAD_ACCEPT}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleFile(file);
        }}
      />

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const file = e.dataTransfer.files?.[0];
          if (file && !isUploading) void handleFile(file);
        }}
        className={cn(
          'flex flex-col items-center gap-2 rounded-card border-2 border-dashed border-primary bg-primary-50/40 px-3.5 py-5 text-center',
          isUploading && 'opacity-60'
        )}
      >
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary">
          <Upload className="h-5 w-5" />
        </div>
        <div className="text-[13px] font-semibold text-ink">
          {t('directSale.design.uploadPanel.dropHint')}
        </div>
        <div className="text-[11.5px] text-mute">
          {t('directSale.design.uploadPanel.formats', { size: MAX_SIZE_MB })}
          <br />
          {t('directSale.design.uploadPanel.minRes')}
        </div>
        <button
          type="button"
          disabled={isUploading}
          onClick={() => fileInputRef.current?.click()}
          className="mt-1 rounded-control bg-primary px-4.5 py-2 text-[12.5px] font-semibold text-primary-fg transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isUploading
            ? t('directSale.design.uploadPanel.uploading')
            : t('directSale.design.chooseFile')}
        </button>
      </div>

      {error && (
        <p role="alert" className="mt-2.5 text-xs font-medium text-danger">
          {t(`directSale.design.uploadPanel.error.${error}`, {
            size: MAX_SIZE_MB,
          })}
        </p>
      )}

      <p className="mt-2.5 text-[11px] leading-relaxed text-mute">
        {t('directSale.design.uploadPanel.afterUpload')}
      </p>
    </div>
  );
}
